/**
 * Lead admin hooks (#21). Mirrors `api/src/leads/admin/admin-leads.routes.ts`:
 *
 *   - GET   /api/admin/leads        — paginated + filtered list
 *   - GET   /api/admin/leads/:id    — single lead with variant join
 *   - PATCH /api/admin/leads/:id    — status (transition-validated) + notes
 *
 * Mutations invalidate `queryKeys.leads.all` so both the list view and the
 * currently-open detail drawer re-fetch immediately after a save. The
 * detail key is also explicitly invalidated by id so the drawer's local
 * `useLeadDetail` re-renders without a flash of stale data.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { LeadAdminResponse, LeadStatus, UpdateLeadInput } from "@ottopilot/shared";
import { apiGet, apiList, apiPatch } from "../lib/api";
import type { ApiClientError, PageMeta } from "../lib/api";
import { queryKeys } from "./queryKeys";

export type LeadsListParams = {
  page: number;
  pageSize: number;
  siteId?: string;
  status?: LeadStatus;
  /** ISO date string, lower bound (inclusive). */
  from?: string;
  /** ISO date string, upper bound (exclusive). */
  to?: string;
  q?: string;
};

export type LeadsListResult = {
  data: LeadAdminResponse[];
  meta: PageMeta | undefined;
};

/**
 * Build the query object passed to `apiList`. Drops undefined filters so
 * the URL stays clean (`?page=1&pageSize=20` instead of
 * `?page=1&pageSize=20&siteId=&status=...`).
 */
function buildLeadsQuery(params: LeadsListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: params.page,
    pageSize: params.pageSize,
  };
  if (params.siteId) out.siteId = params.siteId;
  if (params.status) out.status = params.status;
  if (params.from) out.from = params.from;
  if (params.to) out.to = params.to;
  if (params.q) out.q = params.q;
  return out;
}

export function useLeads(params: LeadsListParams) {
  // Build a stable record for the cache key (TanStack uses deep equality).
  const keyParams: Record<string, string | number | undefined> = {
    page: params.page,
    pageSize: params.pageSize,
    siteId: params.siteId,
    status: params.status,
    from: params.from,
    to: params.to,
    q: params.q,
  };
  return useQuery<LeadsListResult, ApiClientError>({
    queryKey: queryKeys.leads.list(keyParams),
    queryFn: () =>
      apiList<LeadAdminResponse[]>("/api/admin/leads", {
        query: buildLeadsQuery(params),
      }),
    placeholderData: keepPreviousData,
  });
}

export function useLeadDetail(id: string | undefined) {
  return useQuery<LeadAdminResponse, ApiClientError>({
    queryKey: queryKeys.leads.detail(id ?? ""),
    queryFn: () => apiGet<LeadAdminResponse>(`/api/admin/leads/${id ?? ""}`),
    enabled: Boolean(id),
  });
}

export function useUpdateLead(id: string) {
  const qc = useQueryClient();
  return useMutation<LeadAdminResponse, ApiClientError, UpdateLeadInput>({
    mutationFn: (input) => apiPatch<LeadAdminResponse>(`/api/admin/leads/${id}`, input),
    onSuccess: (data) => {
      // Invalidate the entire leads tree (list views with various
      // filters) AND the specific detail key so the drawer updates
      // without a flash.
      void qc.invalidateQueries({ queryKey: queryKeys.leads.all });
      qc.setQueryData(queryKeys.leads.detail(data.id), data);
    },
  });
}
